import { ArchiveBoxArrowDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Notification from '../../components/notification/notification';
import discountApi from '../../services/discountApi';
import publicCouponApi from '../../services/publicCouponApi';
import { PlatformTypes } from '../../data/enums';
import { useSelector } from 'react-redux';
import PublicCouponList from './components/PublicCouponList';
import LegacyCouponList from './components/LegacyCouponList';
import PublicCouponUpsertModal from './components/PublicCouponUpsertModal';
import LegacyCouponUpsertModal from './components/LegacyCouponUpsertModal';
import { PublicCouponType } from './constants';
import { PageHeader, TWButton } from '../../components/ui';
import './coupons.css';

const Coupons = () => {
  const navigate = useNavigate();
  const globalCompany = useSelector((state) => state.companyReducer.selected);

  // Legacy Discount state
  const [couponList, setCouponList] = useState([]);

  // Public Coupon state
  const [publicCouponList, setPublicCouponList] = useState([]);

  const [loading, setLoading] = useState({
    listLoading: true,
    publicListLoading: true,
  });

  // Modal states
  const [showPublicCouponModal, setShowPublicCouponModal] = useState(false);
  const [publicCouponInitialData, setPublicCouponInitialData] = useState(null);
  const [showLegacyCouponModal, setShowLegacyCouponModal] = useState(false);
  const [legacyCouponInitialData, setLegacyCouponInitialData] = useState(null);

  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });

  const [publicCouponPagination, setPublicCouponPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });

  // Generic function to fetch coupon list
  const fetchCouponList = async ({
    api,
    loadingKey,
    setList,
    pagination,
    setPagination,
    useUpperCasePagination = false,
    extraQuery = '',
    filterFn = null,
  }) => {
    setLoading((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const baseQuery = `?companyid=${globalCompany.id}${extraQuery}`;
      const pageParam = useUpperCasePagination ? 'PageNumber' : 'pageNumber';
      const sizeParam = useUpperCasePagination ? 'PageSize' : 'pageSize';
      const query = `${baseQuery}&${pageParam}=${pagination.pageNumber}&${sizeParam}=${pagination.pageSize}`;
      const list = await api.list(query);

      let responseData = list.data?.data || list.data.data || [];
      if (filterFn) {
        responseData = responseData.filter(filterFn);
      }
      setList(responseData);
      setPagination({
        pageNumber: list.data.pageNumber || pagination.pageNumber,
        pageSize: list.data.pageSize || pagination.pageSize,
        totalRecords:
          typeof list.data.totalRecords === 'number'
            ? list.data.totalRecords
            : responseData.length,
      });
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const getCouponList = async () => {
    await fetchCouponList({
      api: discountApi,
      loadingKey: 'listLoading',
      setList: setCouponList,
      pagination,
      setPagination,
      useUpperCasePagination: false,
    });
  };

  const getPublicCouponList = async () => {
    await fetchCouponList({
      api: publicCouponApi,
      loadingKey: 'publicListLoading',
      setList: setPublicCouponList,
      pagination: publicCouponPagination,
      setPagination: setPublicCouponPagination,
      useUpperCasePagination: true,
      // Fetch only Draft(0), Active(2), Disabled(4) — exclude Archived(5)
      extraQuery:
        '&status=0&status=3&status=1&status=2&status=4&orderby=0&ordercolumn=status',
    });
  };

  useEffect(() => {
    getPublicCouponList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany.id, publicCouponPagination.pageNumber]);

  useEffect(() => {
    getCouponList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany.id, pagination.pageNumber]);

  // Modal handlers
  const onClickAddPublicCoupon = () => {
    setPublicCouponInitialData(null);
    setShowPublicCouponModal(true);
  };

  const onClickEditPublicCoupon = (coupon) => {
    setPublicCouponInitialData(coupon);
    setShowPublicCouponModal(true);
  };

  const onClickAddLegacyCoupon = () => {
    setLegacyCouponInitialData(null);
    setShowLegacyCouponModal(true);
  };

  const onClickEditLegacyCoupon = (coupon) => {
    setLegacyCouponInitialData(coupon);
    setShowLegacyCouponModal(true);
  };

  const handlePublicCouponSuccess = () => {
    getPublicCouponList();
  };

  const handleLegacyCouponSuccess = () => {
    getCouponList();
  };

  const handleError = (error) => {
    setNotf((prev) => ({
      ...prev,
      message: error,
      display: true,
    }));
  };

  return (
    <div>
      <div className="coupons-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
        <div className="mx-auto max-w-5xl space-y-8">
          <PageHeader
            title="Coupons"
            description="Manage public campaigns and legacy discount coupons."
          />

          <section className="reveal">
            <div className="flex flex-col gap-4 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-200">
                  Public Campaigns
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Manage public campaigns to all your platforms
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <TWButton
                  variant="default"
                  icon={ArchiveBoxArrowDownIcon}
                  onClick={() => navigate('/dashboard/campaigns/archive')}
                  title="View Archived Campaigns"
                />
                <TWButton
                  variant="primary"
                  icon={PlusIcon}
                  label="Create Public Campaign"
                  onClick={onClickAddPublicCoupon}
                />
              </div>
            </div>

            <PublicCouponList
              publicCouponList={publicCouponList}
              loading={loading.publicListLoading}
              publicCouponPagination={publicCouponPagination}
              onPaginationChange={(page) =>
                setPublicCouponPagination((prev) => ({
                  ...prev,
                  pageNumber: page,
                }))
              }
              onEdit={onClickEditPublicCoupon}
              PublicCouponType={PublicCouponType}
              PlatformTypes={PlatformTypes}
            />
          </section>

          <section className="reveal">
            <div className="flex flex-col gap-4 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-200">
                  Legacy Discounts
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Manage legacy discount coupons
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <TWButton
                  variant="primary"
                  icon={PlusIcon}
                  label="Add Legacy Coupon"
                  onClick={onClickAddLegacyCoupon}
                />
              </div>
            </div>

            <LegacyCouponList
              couponList={couponList}
              loading={loading.listLoading}
              pagination={pagination}
              onPaginationChange={(page) =>
                setPagination((prev) => ({
                  ...prev,
                  pageNumber: page,
                }))
              }
              onEdit={onClickEditLegacyCoupon}
            />
          </section>
        </div>
      </div>

      <PublicCouponUpsertModal
        show={showPublicCouponModal}
        onClose={() => setShowPublicCouponModal(false)}
        initialData={publicCouponInitialData}
        onSuccess={handlePublicCouponSuccess}
        onError={handleError}
      />

      <LegacyCouponUpsertModal
        show={showLegacyCouponModal}
        onClose={() => setShowLegacyCouponModal(false)}
        initialData={legacyCouponInitialData}
        onSuccess={handleLegacyCouponSuccess}
        onError={handleError}
      />

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default Coupons;
